import React, { useState, useMemo } from "react";
import { Trash2, Edit, Search } from "lucide-react";
import { subcategoryService } from "../../services/subcategoryService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteConfirmation from "../common/DeleteConfirmation";
import SubcategoryForm from "./SubcategoryForm";
import debounce from "lodash/debounce";

const SubcategoryList = ({
  subcategories,
  categories,
  viewMode,
  setSubcategories,
  selectedCategory,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const page = subcategories?.page || 1;
  const rawResults = subcategories?.results || subcategories || [];
  // Old backend omits totalPages; a full page (20 items) implies a next page exists
  const totalPages =
    subcategories?.totalPages ?? (rawResults.length >= 20 ? page + 1 : page);

  const fetchPage = async (p, keyword) => {
    try {
      setLoading(true);
      setSubcategories(await subcategoryService.getSubcategories(p, keyword));
    } catch (error) {
      console.error("Failed to fetch subcategories:", error);
      toast.error("Failed to load subcategories!");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useMemo(
    () => debounce((value) => fetchPage(1, value), 500),
    []
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    debouncedSearch(e.target.value);
  };

  // Ensure subcategories is an array and filter
  const filteredSubcategories = selectedCategory
    ? rawResults.filter((sub) => sub.categoryId === selectedCategory.id)
    : rawResults;

  const handleDeleteSubcategory = async () => {
    try {
      setLoading(true);
      await subcategoryService.deleteSubcategory(selectedSubcategory._id);

      const updatedSubcategories = await subcategoryService.getSubcategories(
        page,
        searchTerm
      );
      setSubcategories(updatedSubcategories);

      toast.success("Subcategory deleted successfully!");
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Failed to delete subcategory:", error);
      toast.error("Failed to delete subcategory!");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubcategory = async (formData) => {
    try {
      setLoading(true);
      await subcategoryService.updateSubcategory(
        selectedSubcategory._id,
        formData
      );
      const updatedSubcategories = await subcategoryService.getSubcategories(
        page,
        searchTerm
      );
      setSubcategories(updatedSubcategories);

      setIsEditModalOpen(false);
      toast.success("Subcategory updated successfully!");
    } catch (error) {
      console.error("Failed to update subcategory:", error);
      toast.error("Failed to update subcategory!");
    } finally {
      setLoading(false);
    }
  };

  const renderGridView = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="spinner border-t-4 border-blue-500 w-8 h-8 rounded-full animate-spin"></div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredSubcategories.map((subcategory, index) => (
          <div
            key={subcategory._id || index}
            className="bg-white shadow rounded-lg p-4 flex flex-col items-start"
          >
            <img
              src={subcategory.image}
              alt={subcategory.name || "Subcategory"}
              className="w-full h-32 object-cover rounded-md mb-4"
            />
            <h3 className="text-lg font-bold mb-2">{subcategory.name}</h3>
            <p className="text-gray-600 text-sm mb-2">
              Category: {subcategory.category?.name || "Unassigned"}
            </p>
            {subcategory.category?.promotion?.discounts?.[0] && (
              <div className="text-sm text-green-500 bg-green-100 px-2 py-1 rounded-md mb-2">
                Promotion: Buy{" "}
                {subcategory.category.promotion.discounts[0].buyX}, next gets{" "}
                {subcategory.category.promotion.discounts[0].discountOnNext}%
                off
              </div>
            )}
            <div className="flex space-x-2 mt-auto">
              <button
                onClick={() => {
                  setSelectedSubcategory(subcategory);
                  setIsEditModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700"
              >
                <Edit size={18} />
              </button>
              <button
                onClick={() => {
                  setSelectedSubcategory(subcategory);
                  setIsDeleteModalOpen(true);
                }}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {filteredSubcategories.length === 0 && (
        <p className="text-center text-gray-500">No subcategories found.</p>
      )}
      {filteredSubcategories.map((subcategory, index) => (
        <div
          key={subcategory._id || index}
          className="flex justify-between items-center p-4 border-b last:border-b-0 hover:bg-gray-50"
        >
          <div className="flex items-center space-x-4">
            <img
              src={subcategory.image}
              alt={subcategory.name}
              className="w-12 h-12 object-cover rounded-md"
            />
            <div>
              <h3 className="text-lg font-bold">{subcategory.name}</h3>
              <p className="text-sm text-gray-600">
                Category: {subcategory.category?.name || "Unassigned"}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSelectedSubcategory(subcategory);
                setIsEditModalOpen(true);
              }}
              className="text-blue-500 hover:text-blue-700"
            >
              <Edit size={18} />
            </button>
            <button
              disabled={loading}
              className={`text-red-500 hover:text-red-700 ${
                loading && "opacity-50"
              }`}
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search subcategories..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full px-4 py-2 pl-10 pr-4 border rounded-lg focus:outline-none focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
      </div>

      {viewMode === "grid" ? renderGridView() : renderListView()}

      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mt-6">
          <button
            onClick={() => fetchPage(page - 1, searchTerm)}
            disabled={page <= 1 || loading}
            className={`px-4 py-2 rounded-lg ${
              page <= 1 || loading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Previous
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => fetchPage(page + 1, searchTerm)}
            disabled={page >= totalPages || loading}
            className={`px-4 py-2 rounded-lg ${
              page >= totalPages || loading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Next
          </button>
        </div>
      )}

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteSubcategory}
        itemName="subcategory"
      />

      {selectedSubcategory && (
        <SubcategoryForm
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubcategory}
          initialData={selectedSubcategory}
          categories={categories}
        />
      )}
    </>
  );
};

export default SubcategoryList;
